import { inject } from '@angular/core';
import { Router, CanActivateFn, UrlTree } from '@angular/router';
import { Observable, filter, map, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { PermissionService } from '../services/permission.service';
import { AreaApp, MENUS_POR_AREA, RUTA_INICIO_POR_AREA, firstAllowedRoute } from '../config/menu.catalog';

const RUTA_SELECCION = '/auth/select-profile';

/** Permiso que habilita una ruta, según el catálogo de módulos. */
function permisoDeRuta(area: AreaApp, url: string): string | null {
  const limpia = url.split('?')[0].split('#')[0];

  for (const item of MENUS_POR_AREA[area]) {
    for (const sub of item.submenu ?? []) {
      if (limpia.startsWith(sub.routerLink)) return sub.permiso;
    }
    if (item.routerLink && limpia.startsWith(item.routerLink)) {
      return item.permiso ?? null;
    }
  }
  return null;
}

/** Primera ruta que el rol sí puede abrir, recorriendo sus áreas en orden. */
function primeraRutaPermitida(areas: AreaApp[], permisos: PermissionService): string | null {
  for (const area of areas) {
    const ruta = firstAllowedRoute(area, codigo => permisos.tienePermisoSincrono(codigo));
    if (ruta) return ruta;
  }
  return null;
}

/**
 * Decide a dónde mandar a alguien que no puede estar donde está.
 *
 * Nunca devuelve una URL que este mismo guard volvería a rechazar: eso es lo
 * que producía un bucle de redirección infinito, que el router cancelaba y
 * dejaba la pantalla congelada sin error en consola.
 */
function redirigir(
  router: Router,
  areas: AreaApp[],
  permisos: PermissionService,
  urlActual: string,
): boolean | UrlTree {
  const destino = primeraRutaPermitida(areas, permisos) ?? RUTA_SELECCION;

  // Ya estamos en el destino: dejar pasar antes que rebotar contra uno mismo.
  if (urlActual.startsWith(destino)) return true;

  return router.createUrlTree([destino]);
}

function decidir(
  router: Router,
  auth: AuthService,
  permisos: PermissionService,
  url: string,
): boolean | UrlTree {
  const areas = auth.getActiveAreas();

  // Sin rol elegido, o con uno sin opciones, no hay nada que decidir: que elija primero.
  if (areas.length === 0) {
    return url.startsWith(RUTA_SELECCION) ? true : router.createUrlTree([RUTA_SELECCION]);
  }

  /*
   * Si el rol no tiene ni un permiso de módulo, no se puede distinguir "no
   * tiene acceso" de "este entorno todavía no corrió el seeder de roles".
   * Ante la duda se deja pasar dentro de su propia área: bloquear dejaría la
   * plataforma inutilizable en cuanto se despliegue sin sembrar permisos.
   */
  const sinPermisosDeModulo = !permisos.tieneAlgunPermisoDeModulo();

  // Un rol base tiene una sola área; uno creado a mano, las que le hayan concedido.
  const area = areas.find(a => url.startsWith(`/${a}`));

  // Fuera de sus áreas siempre se corrige, haya permisos o no.
  if (!area) {
    return sinPermisosDeModulo
      ? router.createUrlTree([RUTA_INICIO_POR_AREA[areas[0]]])
      : redirigir(router, areas, permisos, url);
  }

  if (sinPermisosDeModulo) return true;

  // Una ruta que el catálogo no declara (detalles, wizards) hereda el acceso
  // del área: exigirle un permiso que nadie tiene la volvería inalcanzable.
  const permiso = permisoDeRuta(area, url);
  if (!permiso || permisos.tienePermisoSincrono(permiso)) return true;

  return redirigir(router, areas, permisos, url);
}

/**
 * Impide entrar a un área que no corresponde al rol activo, y dentro del área
 * a un módulo que el rol no tiene concedido.
 *
 * Espera a que los permisos estén cargados antes de decidir: en una recarga
 * `verifyToken()` es asíncrono y el guard corría con el conjunto todavía vacío,
 * bloqueando todo.
 */
export const roleGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const auth = inject(AuthService);
  const permisos = inject(PermissionService);

  return auth.permissionsLoaded$.pipe(
    filter(cargado => cargado),
    take(1),
    map(() => decidir(router, auth, permisos, state.url)),
  ) as Observable<boolean | UrlTree>;
};
